import produce from "immer";
import { act } from "./actions";
import { applyStatus } from "./applyStatus";
import { check } from "./conditions";
import { select } from "./targets";
import { IEntity, IGambit, IState } from "../../types";

function performGambit(
  actors: IEntity[],
  sourceIndex: number,
  gambit: IGambit
): IEntity[] | undefined {
  const { target, condition, action, active } = gambit;

  // skip this gambit if it is not active (user-toggable)
  if (!active) return undefined;

  // skip this gambit if no possibleTargets found
  const targetIndex = select(actors, sourceIndex, target);
  if (targetIndex === -1) return undefined;
  const metCondition = check(actors, targetIndex, condition);
  if (!metCondition) return undefined;

  const gambitActors = act(actors, sourceIndex, targetIndex, action);
  if (gambitActors[targetIndex].lastAction?.succeeded === false)
    return undefined;
  return gambitActors;
}

function calculatePoints(draft: IState): number {
  // const a = draft.pick.wave;
  const { battle: currentTurn, battleDuration: maxTurns } = draft.turn;
  const score = Math.abs(currentTurn / maxTurns);
  if (score < 0.3) return 1;
  if (score < 0.8) return 2;
  if (score <= 1) return 5;
  return 0;
}

function handleActorsDeath(draft: IState) {
  const actorHasDied = draft.actors.some(actor => actor.stats.hp.value <= 0);
  if (actorHasDied) {
    draft.screen.previous = draft.screen.current;
    draft.screen.current = "#tech_tree";
    draft.board.points += calculatePoints(draft);
  }
}

export default function turnReducer(state: IState): IState {
  // change turn
  const { actors, turn: prevTurn } = state;
  const turn = prevTurn.battle + 1;
  const activeIndex = turn % actors.length;

  const statusActors = applyStatus(actors, activeIndex);
  let gambitActors: IEntity[] | undefined;

  // find the first gambit that when performed returns an IEntity[]. Exit early
  // `Array.prototype.find` is not used since we want an IEntity[], not an IGambit
  statusActors[activeIndex].gambits.some(gambit => {
    gambitActors = performGambit(statusActors, activeIndex, gambit);
    return gambitActors !== undefined && gambitActors !== statusActors;
  });
  return produce(state, draft => {
    draft.actors = gambitActors ?? statusActors;
    draft.turn.global += 1;
    draft.turn.battle += 1;
    handleActorsDeath(draft);
  });
}
